import {
  mdiPlus,
  mdiHistory,
  mdiArrowTopRight,
  mdiFilterVariant,
} from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  useToast,
  ToastType,
} from "../../../context_providers/toast/toast_ctx";
import { fetchGet, toQueryString } from "../../../service/api_client";
import { urls } from "../../../utils/urls";
import { useAuth } from "../../../context_providers/auth/authProvider";
import { ACTION, MODULE } from "../../../utils/enums";
import Pagination from "../../../ui/new_components/common/pagination";
import { debounce } from "lodash";
import DrawerModal, {
  DrawerModalLoaction,
} from "../../../ui/new_components/common/drawer_modal";
import EntityHistory, {
  GetMasterLogsByEntityApi,
} from "../../purchase_order/purchase_order_create/components/entity_logs";
import { CostCenter, CostCenterFilterData } from "../models/cost_center_model";
import moment from "moment";
import {
  Project,
  ProjectCloseStatus,
  ProjectFilterData,
} from "../models/project_model";
import SearchFieldWidget from "../../../ui/new_components/search_field";
import FilterDropDown from "../../../ui/new_components/common/filter_drop_down";
import ProjectMultiSelect from "../../accounts/purchase/repo/project_multi_search_field";
import { CostCenterMultiFilter } from "../../../ui/new_components/entity_selects/EntityMultiFilters";
import { formatAmountWithCommas } from "../../../ui/pages/content_panel/utils/helpers";
import {
  ResponsiveListShell,
  FilterSheet,
  DateRangeFilter,
  MobileRecordCard,
  MobileRecordCardList,
  StatusBadge,
  EmptyState,
} from "../../../ui/new_components/common/responsive";

interface ProjectScreenProps {}

const defaultFilter: ProjectFilterData = {
  page: 1,
  count: 100,
};

const ProjectListScreen: FunctionComponent<ProjectScreenProps> = () => {
  const cc_url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/cost_center`;
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/project`;

  const [filter, setFilter] = useState<ProjectFilterData>(defaultFilter);
  const { showToast } = useToast();
  const [maker, setMaker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(50);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<Project[]>([]);
  const [costCenter, setCostCenter] = useState<CostCenter[]>([]);
  const { isAuthorised } = useAuth();
  const [selectedItems, setSelectedItems] = useState<Project[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<Project[]>([]);
  const [showHistory, setShowHistory] = useState<string>();
  const [showFilter, setShowFilter] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 768,
  );
  const navaigation = useHistory();

  const fetchByCostCenter = async (prop: ProjectFilterData) => {
    const ccIds = prop.cost_center_id ?? [];
    const term = (prop.search ?? "").trim().toLowerCase();
    const res = await fetchGet(
      url +
        toQueryString({
          ...prop,
          search: undefined,
          cost_center_id: undefined,
          all: true,
        }),
    );
    if (!res.success) {
      showToast({ type: ToastType.error, text: res.error });
      return;
    }
    const matched = ((res.data?.data ?? []) as Project[]).filter(
      (p) =>
        (!ccIds.length || ccIds.includes(p.cost_center_id)) &&
        (!term ||
          [p.id, p.name, p.cost_center_id].some((v) =>
            (v ?? "").toLowerCase().includes(term),
          )),
    );
    const page = prop.page || 1;
    const pageSize = prop.count || 100;
    setData(matched.slice((page - 1) * pageSize, page * pageSize));
    setCount(pageSize);
    setTotal(matched.length);
  };

  const fetchData = async (prop: ProjectFilterData) => {
    setLoading(true);

    try {
      if (
        (prop.cost_center_id?.length ?? 0) > 0 ||
        /^cc-/i.test((prop.search ?? "").trim())
      ) {
        await fetchByCostCenter(prop);
        setLoading(false);
        return;
      }
      const res = await fetchGet(url + toQueryString(prop));
      if (res.success) {
        setData(res.data.data);
        setCount(res.data.metadata?.count);

        if (res.data.metadata) {
          setTotal(res.data.metadata.total);
        }
        setLoading(false);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    }
  };

  const fetchDataCostCenter = async (prop: CostCenterFilterData) => {
    setLoading(true);

    try {
      const res = await fetchGet(cc_url + toQueryString(prop));
      if (res.success) {
        setCostCenter(res.data.data);
        setLoading(false);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    }
  };

  useEffect(() => {
    const maker_permission_result = isAuthorised({
      module: MODULE.project_master,
      action: [ACTION.CREATE, ACTION.UPDATE],
      operator: "or",
    });

    setMaker(maker_permission_result);
  }, [isAuthorised]); // Separate call for permission check

  const debouncedHandler = useCallback(
    debounce((prop: ProjectFilterData) => {
      if ("page" in prop) {
        fetchData(prop);
      } else {
        fetchDataCostCenter(prop);
      }
    }, 500),
    [],
  );

  useEffect(() => {
    debouncedHandler(filter);
  }, [page, filter, debouncedHandler]);

  const ExportProjectListData = async () => {
    try {
      const a = document.createElement("a");
      a.target = "_blank";
      a.href = `${
        process.env.REACT_APP_BACKEND_BASE_URL
      }/api/v3/erp/inventory/project/export${toQueryString({
        query: JSON.stringify({
          ...filter,
        }),
      })}`;

      // Delay the trigger to allow UI updates (if necessary)
      setTimeout(() => {
        setLoading(false);
        showToast({ type: ToastType.success, text: "Download started!" });
        a.click(); // Trigger the download
      }, 1000);
    } catch (error) {
      setLoading(false); // Make sure loading is set to false in case of an error
      showToast({ type: ToastType.error, text: "Failed to start download." });
      console.error("Download error:", error);
    }
  };

  const onSelect = (d: Project) => {
    navaigation.push(urls.procurement_project + "/view-project/" + d.id);
  };

  const handleOnEdit = () => {
    if (selectedItems.length > 1) {
      showToast({
        type: ToastType.error,
        text: "Please select one item to edit",
      });
    } else if (selectedItems.length === 0) {
      showToast({
        type: ToastType.error,
        text: "Please select an item to edit",
      });
    } else {
      navaigation.push(
        urls.procurement_project + "/update/" + selectedItems[0]!.id,
      );
    }
  };

  return (
    <>
      {showHistory && (
        <DrawerModal
          location={DrawerModalLoaction.right}
          show={showHistory !== undefined}
          onClose={() => setShowHistory(undefined)}
          title="Project History"
        >
          <EntityHistory
            fetchLogs={({ page, count }) =>
              GetMasterLogsByEntityApi({
                entityType: "project",
                entityId: showHistory,
                page,
                count,
              })
            }
          />
        </DrawerModal>
      )}

      <ResponsiveListShell
        title="Project Master"
        count={total}
        onRefresh={() => fetchData({ page, count })}
        search={
          <div id="project_id_filter">
            <SearchFieldWidget
              placeholder="Project ID / Name / CC ID"
              defaulValue={filter?.search}
              onClear={() => {
                setFilter({ ...filter, search: undefined });
                setPage(1);
              }}
              onSubmit={(val) => {
                setFilter({ ...filter, search: val });
                setPage(1);
              }}
            />
          </div>
        }
        actions={
          <>
            <button
              id="export_btn"
              onClick={() => {
                ExportProjectListData();
              }}
              className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
            >
              <Icon path={mdiArrowTopRight} size={0.8} />
              Export
            </button>
            {maker && (
              <button
                id="create_project_btn"
                onClick={() => {
                  navaigation.push(urls.procurement_project + "/create");
                }}
                className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
              >
                <Icon path={mdiPlus} className="h-4 w-4"></Icon>Create
              </button>
            )}
            {maker && (
              <button
                id="update_project_btn"
                onClick={handleOnEdit}
                className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
              >
                <Icon path={mdiPlus} className="h-4 w-4"></Icon>Update
              </button>
            )}
          </>
        }
        filterToggle={
          <div className="flex gap-2 items-center">
            <h6 className="font-normal text-xs">Filters</h6>
            <div
              id="project_list_filter_toggle_btn"
              onClick={() => setShowFilter((v) => !v)}
              className="flex items-center justify-center bg-gray-200 p-1 rounded-full hover:bg-gray-800 hover:text-white cursor-pointer"
            >
              <Icon path={mdiFilterVariant} className="w-4 h-4" />
            </div>
          </div>
        }
      >
        <FilterSheet
          show={showFilter}
          onClose={() => setShowFilter(false)}
          onApply={() => setShowFilter(false)}
          onReset={() => {
            setFilter(defaultFilter);
            setSelectedProjects([]);
          }}
          gridClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-2 items-start"
        >
          <div id="project_created_at_filter">
            <label>Created At</label>
            <DateRangeFilter
              compact
              showChips={false}
              fromDate={
                filter.from ? moment(filter?.from).format("YYYY-MM-DD") : ""
              }
              toDate={filter.to ? moment(filter?.to).format("YYYY-MM-DD") : ""}
              onFromChange={(v) =>
                setFilter({
                  ...filter,
                  from: v
                    ? new Date(new Date(v).setHours(0, 0, 0, 0))
                    : undefined,
                })
              }
              onToChange={(v) =>
                setFilter({
                  ...filter,
                  to: v
                    ? new Date(new Date(v).setHours(0, 0, 0, 0))
                    : undefined,
                })
              }
            />
          </div>
          <div id="project_name_filter">
            <label className="text-md pt-1">Project Name</label>
            <ProjectMultiSelect
              onSelect={(selectedProject: Project) => {
                setFilter((prevFilter) => {
                  if (prevFilter.name?.some((w) => w === selectedProject.id)) {
                    return prevFilter;
                  }
                  return {
                    ...prevFilter,
                    name: [...(prevFilter?.name || []), selectedProject.id],
                  };
                });

                setSelectedProjects((prevProjects) => {
                  if (prevProjects.some((v) => v.id === selectedProject.id)) {
                    return prevProjects;
                  }
                  return [...prevProjects, selectedProject];
                });
              }}
              onDelete={(id: string) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  name: (prevFilter?.name || []).filter((w) => w !== id),
                }));

                setSelectedProjects((prevProjects) =>
                  prevProjects.filter((v) => v.id !== id),
                );
              }}
              selectedProjects={selectedProjects ?? []}
            />
          </div>
          <div id="cost_center_filter">
            <label className="text-md pt-1">Cost Center</label>
            <CostCenterMultiFilter
              values={filter.cost_center_id}
              onChange={(cost_center_id) => {
                setFilter({ ...filter, cost_center_id });
                setPage(1);
              }}
            />
          </div>
          <div id="project_status_filter">
            <label className="text-md">Project Status</label>
            <FilterDropDown
              placeholder="Project Status"
              onClearAll={() =>
                setFilter({ ...filter, projectStatus: undefined })
              }
              onDelete={(type) => {
                setFilter({
                  ...filter,
                  projectStatus: filter?.projectStatus?.filter(
                    (f: any) => f !== type,
                  ),
                });
              }}
              onSelect={(type) => {
                setFilter((old) => ({
                  ...old,
                  projectStatus: [...(old?.projectStatus ?? []), type],
                }));
              }}
              selectedOptions={filter?.projectStatus ?? []}
              options={Object.values(ProjectCloseStatus).map((t) => ({
                label: t,
                value: t,
              }))}
            />
          </div>
        </FilterSheet>

        {data.length > 0 ? (
          <>
            <div className="hidden md:block my-2 border">
              <div className="overflow-auto">
                <table
                  id="project_list_table"
                  className="w-full text-xs divide-y divide-gray-200 relative"
                >
                  <thead className="text-xs py-2 bg-white z-10">
                    <tr className="border">
                      <th>
                        <div className="">
                          <input
                            type="checkbox"
                            name=""
                            id=""
                            checked={
                              selectedItems.length === data.length
                                ? true
                                : false
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedItems([...data]);
                              } else {
                                setSelectedItems([]);
                              }
                            }}
                          />
                        </div>
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-center">
                        History
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Project ID
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Name
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left min-w-[200px]">
                        Description
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Cost Center
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Batch Name
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-right">
                        Batch Size
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-right">
                        Budget
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-right">
                        Additional Funds
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-right">
                        Total Budget
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-right">
                        Spent
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-right">
                        Balance
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Project Status
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Created By
                      </th>
                    </tr>
                  </thead>
                  <tbody className="">
                    {data.map((project, i) => {
                      const isSelected = selectedItems.includes(project);
                      return (
                        <tr
                          id={`project_row_${project.id}`}
                          className="border text-center "
                          key={i}
                        >
                          <td>
                            <div className="p-2">
                              <input
                                type="checkbox"
                                name=""
                                id=""
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedItems((prev) => [
                                      ...prev,
                                      project,
                                    ]);
                                  } else {
                                    setSelectedItems((prev) =>
                                      prev.filter(
                                        (selectedItem) =>
                                          selectedItem !== project,
                                      ),
                                    );
                                  }
                                }}
                              />
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-center">
                            <div
                              id={`project_history_btn_${project.id}`}
                              className="cursor-pointer flex justify-center"
                              title="History"
                              onClick={() => setShowHistory(project.id)}
                            >
                              <Icon path={mdiHistory} size="16"></Icon>
                            </div>
                          </td>
                          <td
                            className="px-3 py-3 whitespace-nowrap text-left cursor-pointer text-blue-600 hover:underline"
                            role="button"
                            onClick={() => onSelect(project)}
                          >
                            {project.id}
                          </td>
                          <td
                            className="px-3 py-3 text-left break-words "
                            style={{ minWidth: "200px", maxWidth: "300px" }}
                          >
                            {project.name}
                          </td>
                          <td
                            className="px-3 py-3 text-left break-words "
                            style={{ minWidth: "200px", maxWidth: "300px" }}
                          >
                            {project.description}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-left">
                            {project.cost_center_category}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-left">
                            {project?.batchName ?? "--"}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right">
                            {formatAmountWithCommas(project?.batchSize) ?? "--"}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right">
                            {formatAmountWithCommas(project?.budget) ?? "--"}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right">
                            {formatAmountWithCommas(
                              project?.totalAdditionalFunds,
                            ) ?? "--"}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right">
                            {formatAmountWithCommas(
                              project?.lastUpdatedBudget,
                            ) ?? "--"}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right">
                            {formatAmountWithCommas(project.spent) ?? "--"}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right">
                            {formatAmountWithCommas(
                              project?.lastUpdatedBudget - project?.spent,
                            )}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-left">
                            <StatusBadge
                              status={project.projectStatus}
                              kind="generic"
                            />
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-left">
                            {project.created_by_name ?? "--"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <MobileRecordCardList>
              {data.map((project, i) => (
                <MobileRecordCard
                  id={`project_row_mobile_${project.id}`}
                  key={i}
                  onClick={() => onSelect(project)}
                  title={project.id}
                  status={
                    <StatusBadge
                      status={project.projectStatus}
                      kind="generic"
                    />
                  }
                  fields={[
                    { label: "Name", value: project.name },
                    {
                      label: "Cost Center",
                      value: project.cost_center_category,
                    },
                    {
                      label: "Total Budget",
                      value:
                        formatAmountWithCommas(project?.lastUpdatedBudget) ??
                        "-",
                    },
                    {
                      label: "Spent",
                      value: formatAmountWithCommas(project.spent) ?? "-",
                    },
                    {
                      label: "Created By",
                      value: project.created_by_name ?? "-",
                    },
                  ]}
                  actions={[
                    {
                      icon: mdiHistory,
                      title: "History",
                      onClick: () => setShowHistory(project.id),
                    },
                  ]}
                />
              ))}
            </MobileRecordCardList>

            <div
              id="pagination_container"
              className="mt-3 sticky bottom-0 bg-white z-10 border-t flex justify-between items-center px-4 py-2"
            >
              <Pagination
                itemCount={count}
                page={filter.page}
                total={total}
                count={filter.count}
                onChange={(page) => setFilter((o) => ({ ...o, page }))}
              />
            </div>
          </>
        ) : !loading ? (
          <EmptyState
            title="No Projects found!"
            message="Try adjusting your search or filters."
            imageSrc="/images/nodata.png"
          />
        ) : null}
      </ResponsiveListShell>

      {loading && <LoadingWidget></LoadingWidget>}
    </>
  );
};

export default ProjectListScreen;
